import React, { useState, useImperativeHandle, forwardRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

const NotesSection = forwardRef(({ employeeId, companyUID, notes, currentUser, onAddNote, loading }, ref) => {
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(4);
  const textareaRef = React.useRef(null);
  const sectionRef = React.useRef(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            // Highlight briefly
            textareaRef.current.style.borderColor = 'rgba(86, 141, 237, 0.8)';
            textareaRef.current.style.backgroundColor = 'rgba(86, 141, 237, 0.15)';
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.style.borderColor = 'rgba(86, 141, 237, 0.3)';
                textareaRef.current.style.backgroundColor = 'rgba(86, 141, 237, 0.1)';
              }
            }, 1000);
          }
        }, 300);
      }
    }
  }));

  const handleSubmit = async () => {
    if (!newNote.trim()) return;
    setAdding(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/employee/add-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          note: newNote,
          authorUID: currentUser?.uid || 'system',
          authorName: currentUser?.displayName || currentUser?.email || 'Manager'
        })
      });
      
      const result = await response.json();
      if (result.success) {
        setNewNote('');
        onAddNote?.();
      } else {
        alert('Failed to add note: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Add note failed:', error);
      alert('Failed to add note');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={sectionRef}>
      <div style={{ marginBottom: 20 }}>
        <textarea
          ref={textareaRef}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note about this employee..."
          disabled={adding}
          style={{
            width: '100%',
            minHeight: 80,
            fontFamily: 'Montserrat',
            fontSize: '14px',
            color: '#FFFFFF',
            backgroundColor: 'rgba(86, 141, 237, 0.1)',
            border: '1px solid rgba(86, 141, 237, 0.3)',
            borderRadius: 8,
            padding: 12,
            resize: 'vertical',
            marginBottom: 8,
            transition: 'all 0.3s ease'
          }}
          className="notes-textarea"
        />
        <button
          onClick={handleSubmit}
          disabled={adding || !newNote.trim()}
          style={{
            padding: '10px 20px',
            backgroundColor: adding || !newNote.trim() ? 'rgba(86, 141, 237, 0.2)' : '#568ded',
            border: 'none',
            borderRadius: 6,
            color: '#FFFFFF',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 600,
            cursor: adding || !newNote.trim() ? 'not-allowed' : 'pointer'
          }}
        >
          {adding ? 'Adding...' : 'Add Note'}
        </button>
      </div>
      
      {loading ? (
        <div style={{ color: '#FFFFFF', opacity: 0.7 }}>Loading notes...</div>
      ) : !notes || notes.length === 0 ? (
        <div style={{ color: '#FFFFFF', opacity: 0.7 }}>No notes yet.</div>
      ) : (
        <>
          {notes.slice(0, displayLimit).map((note, idx) => (
          <div key={note.id || idx} style={{
            backgroundColor: 'rgba(86, 141, 237, 0.08)',
            border: '1px solid rgba(86, 141, 237, 0.15)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 12
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8
            }}>
              <span style={{
                fontFamily: 'Montserrat',
                fontSize: '13px',
                fontWeight: 600,
                color: '#FFFFFF'
              }}>
                {note.authorName}
              </span>
              <span style={{
                fontFamily: 'Montserrat',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>
                {new Date(note.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div style={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: 1.5
            }}>
              {note.text}
            </div>
          </div>
          ))}
          
          {/* Load More Button */}
          {notes.length > displayLimit && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                onClick={() => setDisplayLimit(prev => prev + 10)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#568ded',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0
                }}
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

NotesSection.displayName = 'NotesSection';

export default NotesSection;

